import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Modal } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';
import { getReservationDocsData } from '../../../api/Reservation';
import { useAlertModal } from '../../../common/hooks';
import { msgStr } from '../../../common/constants/Message';
import { API_URL } from '../../../common/constants/AppConstants';

interface Props {
  reservationInfo: any;
}

const BACTestsTab = ({ reservationInfo }: Props) => {
  const [docsData, setDocsData] = useState<any[]>([]);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<any>(null);
  const { showAlert } = useAlertModal();

  const fetchDocsData = useCallback(() => {
    if (!reservationInfo?.id) return;
    getReservationDocsData({ reservation_id: reservationInfo.id }, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setDocsData(
            (jsonRes?.documents || [])
              .filter((doc: any) => doc.file_url && doc.file_url !== 'pending_upload' && doc.document_type === 'BAC_TEST')
              .map((doc: any) => {
                let parsedMetadata = doc?.metadata ?? {};
                if (typeof parsedMetadata === 'string') {
                  try {
                    parsedMetadata = JSON.parse(parsedMetadata);
                  } catch (_e) {
                    parsedMetadata = {};
                  }
                }
                return { ...doc, metadata: parsedMetadata };
              })
          );
          break;
        default:
          setDocsData([]);
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  }, [reservationInfo?.id, showAlert]);

  useEffect(() => {
    fetchDocsData();
  }, [fetchDocsData]);

  const getBACScoreColor = (score?: number) => {
    const bacLimit = reservationInfo?.store?.bac_limit ?? 0.08;
    
    if (!score && score !== 0) return '#6c757d';
    if (score === 0) return '#28a745';
    
    const warningThreshold = bacLimit * 0.75;
    
    if (score <= warningThreshold) return '#28a745';
    if (score <= bacLimit) return '#ffc107';
    return '#dc3545';
  };

  const getBACResultBadge = (result?: string, score?: number) => {
    const bacLimit = reservationInfo?.store?.bac_limit ?? 0.08;
    
    if (result === 'pass') {
      return { text: 'PASS', color: '#28a745' };
    } else if (result === 'fail') {
      return { text: 'FAIL', color: '#dc3545' };
    } else if (score !== undefined && score !== null) {
      return score <= bacLimit ? { text: 'PASS', color: '#28a745' } : { text: 'FAIL', color: '#dc3545' };
    }
    return { text: 'N/A', color: '#6c757d' };
  };

  const openVideoPreview = (doc: any) => {
    setSelectedVideo(doc);
    setShowVideoModal(true);
  };

  const closeVideoPreview = () => {
    setShowVideoModal(false);
    setSelectedVideo(null);
  };

  return (
    <View style={styles.container}>
      {docsData.length > 0 && (
        <View style={styles.headerContainer}>
          <Text style={styles.docsHeader}>BAC Test Results</Text>
          <Text style={styles.docsSubheader}>{docsData.length} test{docsData.length !== 1 ? 's' : ''}</Text>
        </View>
      )}
      <ScrollView 
        showsVerticalScrollIndicator={true}
        persistentScrollbar={true}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        {docsData.length > 0 ? (
          <View style={styles.cardsContainer}>
            {docsData.map((doc) => {
              const bacScore = doc.metadata?.bac_score;
              const bacResult = doc.metadata?.bac_result;
              const ownerName = doc.metadata?.owner?.name || 'Unknown';
              const resultBadge = getBACResultBadge(bacResult, bacScore);
              
              return (
                <TouchableOpacity 
                  key={doc.id} 
                  style={styles.card}
                  onPress={() => openVideoPreview(doc)}
                  activeOpacity={0.7}
                >
                  <View style={styles.cardContent}>
                    <View style={styles.leftSection}>
                      <Text style={styles.driverName}>{ownerName}</Text>
                      <View style={styles.scoreRow}>
                        <Text style={styles.scoreLabel}>BAC:</Text>
                        <Text style={[styles.scoreValue, { color: getBACScoreColor(bacScore) }]}>
                          {bacScore !== undefined && bacScore !== null ? bacScore.toFixed(3) : 'N/A'}
                        </Text>
                      </View>
                    </View>
                    <View style={styles.rightSection}>
                      <View style={[styles.resultBadge, { backgroundColor: resultBadge.color }]}>
                        <Text style={styles.resultBadgeText}>{resultBadge.text}</Text>
                      </View>
                      <FontAwesome5 name="play-circle" size={24} color="#007bff" style={{ marginTop: 8 }} />
                    </View>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        ) : (
          <View style={styles.emptyState}>
            <FontAwesome5 name="vial" size={48} color="#ccc" />
            <Text style={styles.emptyText}>No BAC test results available</Text>
          </View>
        )}
      </ScrollView>

      <Modal
        visible={showVideoModal}
        transparent={true}
        animationType="fade"
        onRequestClose={closeVideoPreview}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.videoModalContent}>
            <View style={styles.videoModalHeader}>
              <View>
                <Text style={styles.videoModalTitle}>
                  {selectedVideo?.metadata?.owner?.name || 'BAC Test Video'}
                </Text>
                {selectedVideo?.metadata?.bac_score !== undefined && (
                  <Text style={styles.videoModalSubtitle}>
                    BAC Score: {selectedVideo.metadata.bac_score.toFixed(3)}
                  </Text>
                )}
              </View>
              <TouchableOpacity onPress={closeVideoPreview} style={styles.closeButton}>
                <FontAwesome5 name="times" size={24} color="#333" />
              </TouchableOpacity>
            </View>

            <View style={styles.videoContainer}>
              {selectedVideo?.file_url && (
                <video
                  controls
                  autoPlay
                  style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#000',
                    borderRadius: 8,
                  }}
                >
                  <source src={API_URL + '/uploads/' + selectedVideo.file_url} type="video/mp4" />
                  <source src={API_URL + '/uploads/' + selectedVideo.file_url} type="video/quicktime" />
                  Your browser does not support the video tag.
                </video>
              )}
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { 
    flex: 1, 
    backgroundColor: '#f8f9fa', 
    width: "100%", 
    maxHeight: 510,
    borderRadius: 8,
    overflow: 'hidden'
  },
  headerContainer: {
    padding: 16,
    paddingBottom: 12,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  docsHeader: { 
    fontSize: 18, 
    fontWeight: 'bold', 
    color: '#333',
  },
  docsSubheader: {
    fontSize: 13,
    color: '#6c757d',
    marginTop: 4,
  },
  scrollView: {
    flex: 1
  },
  scrollContent: {
    padding: 16,
  },
  cardsContainer: {
    gap: 12,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    padding: 16,
  },
  cardContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  leftSection: {
    flex: 1,
  },
  driverName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  scoreRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 6,
  },
  scoreLabel: {
    fontSize: 13,
    color: '#6c757d',
    fontWeight: '500',
  },
  scoreValue: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  rightSection: {
    alignItems: 'center',
    gap: 8,
  },
  resultBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  resultBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#fff',
    letterSpacing: 0.5,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 80,
  },
  emptyText: { 
    marginTop: 16, 
    fontSize: 16, 
    color: '#666' 
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  videoModalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '90%',
    maxWidth: 800,
    maxHeight: '85%',
    overflow: 'hidden',
  },
  videoModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  videoModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  videoModalSubtitle: {
    fontSize: 14,
    color: '#6c757d',
    marginTop: 4,
  },
  closeButton: {
    padding: 4,
  },
  videoContainer: {
    width: '100%',
    aspectRatio: 16 / 9,
    backgroundColor: '#000',
  },
});

export default BACTestsTab;
